import * as yup from 'yup';
import { gql } from '__generated__';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetPrivacyForProjectQuery as GetPrivacyForProject,
} from '__generated__/graphql';

import { TranslatableEditor } from '../Translations/Inputs';
import { getServerErrors } from '../../common/helpers';
import { translationsForAttribute } from '../Translations/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';
import useValidation from '../../common/useValidation';

const editPrivacySchema = yup.object().shape({
  privacy_statement: yup.string().nullable(),
});

gql(`
  fragment PrivacyFields on Project {
    id
    privacy_statement
    locale
    translations {
      ...TranslationFields
    }
  }
`);

const EditPrivacyMutation = gql(`
  mutation EditPrivacy($input: EditPrivacyInput!) {
    editPrivacy(input: $input) {
      ...PrivacyFields
    }
  }
`);

const getValues = (project: GetPrivacyForProject['project']) => ({
  privacy_statement: project.privacy_statement,
  privacy_statement_translations: translationsForAttribute(project.translations, 'privacy_statement'),
});

const GetPrivacyForProjectQuery = gql(`
  query GetPrivacyForProject($id: ID!) {
    project(id: $id) {
      ...PrivacyFields
    }
  }
`);

interface EditPrivacyPageProps {
  projectId: string;
}

const EditPrivacyPage = ({ projectId }: EditPrivacyPageProps) => {
  const { data } = useQuery(GetPrivacyForProjectQuery, {
    variables: {
      id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return null;
  }

  return <EditPrivacyView project={project} />;
};

const EditPrivacyView = ({ project }: GetPrivacyForProject) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: getValues(project),
  });

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editPrivacy, { error, loading }] = useMutation(
    EditPrivacyMutation, {
      variables: {
        input: {
          ...form.values,
          project: {
            id: project.id,
          },
        },
      },
      refetchQueries: ['Session'],
      onCompleted: ({ editPrivacy: project }) => {
        notifier.success(t('notifications.project_saved'));
        form.reset(getValues(project));
        setFormKey((key) => key + 1);
      },
      onError: () => {
        scrollTo('ProjectTab');
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: editPrivacySchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.GridContainer>
        <UI.Div>
          <UI.H3>
            {t('privacy_statement')}
          </UI.H3>

          <UI.InputDescription sc={{ mt: 1 }}>
            {t('privacy_statement_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.Form onSubmit={() => editPrivacy()} key={formKey}>
          <UI.Card>
            <UI.FormGrid>
              <UI.ServerErrors error={error} />

              <UI.InputGroup
                sc={{ valid: !errors.privacy_statement, touched: form.touched.privacy_statement }}
              >
                <UI.InputLabel id="PrivacyStatementLabel">
                  {t('privacy_statement')}
                </UI.InputLabel>
                <TranslatableEditor
                  form={form}
                  errors={errors}
                  name="privacy_statement"
                  defaultLocale={project.locale}
                  title={t('privacy_statement')}
                  attributes={{
                    'aria-labelledby': 'PrivacyStatementLabel',
                  }}
                />
                <UI.ErrorMessages
                  attribute={t('privacy_statement')}
                  errors={errors.privacy_statement}
                />
              </UI.InputGroup>

              <UI.HR />

              <UI.FormButtons
                form={form}
                submitLabel={t('save')}
                loading={loading}
                valid={valid}
              />
            </UI.FormGrid>
          </UI.Card>
        </UI.Form>
      </UI.GridContainer>
    </UI.Container>
  );
};

export default EditPrivacyPage;
